import { addMonths, format, parse } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import {
  ApiCreateQuestPayload,
  ApiQuest,
  ApiQuestType,
  ApiUpdateQuestPayload,
  ClimbType,
} from "griptonite-api-client/types";
import isEqual from "lodash/isEqual";
import { z } from "zod";

// A points quest always covers exactly one calendar month in the venue's timezone (the API
// rejects anything else), so the form asks for the month and derives both times from it.
const MONTH_FORMAT = "yyyy-MM";

const QuestDetailsFormSchema = z
  .object({
    type: z.enum(["WORKOUT", "LEADERBOARD_POINTS"]),
    name: z.string({ message: "Name required" }).min(1, { message: "Name required" }),
    description: z.string().nullable(),
    startTime: z.string().datetime().nullable(),
    endTime: z.string().nullable().optional(),
    month: z.string().nullable(),
    isEndless: z.boolean().default(false),
    coverImage: z.object({ uuid: z.string() }).nullable(),
    climbTypes: z.array(z.string()),
  })
  .superRefine((obj, ctx) => {
    if (obj.type === "LEADERBOARD_POINTS") {
      if (!obj.climbTypes.length) {
        ctx.addIssue({
          message: "Choose at least one discipline",
          code: z.ZodIssueCode.custom,
          path: ["climbTypes"],
        });
      }
      if (!obj.month) {
        ctx.addIssue({
          message: "Month required",
          code: z.ZodIssueCode.custom,
          path: ["month"],
        });
      }
      return;
    }
    if (!obj.startTime) {
      ctx.addIssue({
        message: "Start time required",
        code: z.ZodIssueCode.custom,
        path: ["startTime"],
      });
    }
    if (!obj.isEndless && !obj.endTime) {
      ctx.addIssue({
        message: "End time required when not endless",
        code: z.ZodIssueCode.custom,
        path: ["endTime"],
      });
    } else if (
      !obj.isEndless &&
      obj.startTime &&
      obj.endTime &&
      new Date(obj.startTime).getTime() >= new Date(obj.endTime).getTime()
    ) {
      ctx.addIssue({
        message: "End time must be after start time",
        code: z.ZodIssueCode.custom,
        path: ["endTime"],
      });
    }
  });

export type QuestDetailsFormValues = z.infer<typeof QuestDetailsFormSchema>;

// Also rejects a month that's already over, unless it's the one already saved, since a quest that
// has started keeps its month. The current month is worked out on each validation, so a form left
// open past the end of a month still catches it.
export const makeQuestDetailsFormSchema = (timezone: string, savedMonth: string | null) =>
  QuestDetailsFormSchema.superRefine((obj, ctx) => {
    if (
      obj.type === "LEADERBOARD_POINTS" &&
      obj.month &&
      obj.month !== savedMonth &&
      obj.month < currentMonth(timezone)
    ) {
      ctx.addIssue({
        message: "Month can't be in the past",
        code: z.ZodIssueCode.custom,
        path: ["month"],
      });
    }
  });

// A quest with no saved disciplines starts out counting every one the venue offers. Without a
// quest, the values are a blank one of `type`.
export const questToDetailsFormValues = (
  quest: ApiQuest | undefined,
  timezone: string,
  venueClimbTypes: ClimbType[],
  type: ApiQuestType = "WORKOUT",
): QuestDetailsFormValues => ({
  type: quest?.type ?? type,
  name: quest?.name ?? "",
  description: quest?.description ?? null,
  startTime: quest?.startTime ?? null,
  endTime: quest?.endTime ?? null,
  month:
    quest?.type === "LEADERBOARD_POINTS" && quest.startTime
      ? format(toZonedTime(quest.startTime, timezone), MONTH_FORMAT)
      : null,
  isEndless: !!quest && quest.endTime === null,
  coverImage: quest?.coverImage ?? null,
  climbTypes: quest?.climbTypes?.length ? quest.climbTypes : venueClimbTypes,
});

// The month it is now in the venue's timezone, as "yyyy-MM".
export const currentMonth = (timezone: string) =>
  format(toZonedTime(new Date(), timezone), MONTH_FORMAT);

// The first instant of the month, then of the following month, as the API expects.
const monthToPeriod = (month: string, timezone: string) => {
  const first = parse(month, MONTH_FORMAT, new Date());
  const startOf = (date: Date) =>
    fromZonedTime(`${format(date, "yyyy-MM-dd")}T00:00:00`, timezone).toISOString();
  return { startTime: startOf(first), endTime: startOf(addMonths(first, 1)) };
};

// Only fields that changed are sent: once a quest has started the API refuses any change to its
// start time or disciplines, so resending an unchanged one could turn an unrelated edit into an
// error. Disciplines are also sent when nothing is saved yet, since the API needs them to publish.
export const questDetailsFormValuesToPayload = (
  values: QuestDetailsFormValues,
  initial: QuestDetailsFormValues,
  timezone: string,
  savedClimbTypes: ClimbType[] | null,
): ApiUpdateQuestPayload => {
  const payload: ApiUpdateQuestPayload = {};
  if (values.name !== initial.name) payload.name = values.name;
  if (values.description !== initial.description) payload.description = values.description;
  if (values.coverImage?.uuid !== initial.coverImage?.uuid) {
    payload.coverImage = values.coverImage?.uuid ?? null;
  }

  if (values.type === "LEADERBOARD_POINTS") {
    if (
      !savedClimbTypes?.length ||
      !isEqual([...values.climbTypes].sort(), [...savedClimbTypes].sort())
    ) {
      payload.climbTypes = values.climbTypes as ClimbType[];
    }
    if (values.month && values.month !== initial.month) {
      Object.assign(payload, monthToPeriod(values.month, timezone));
    }
    return payload;
  }
  if (values.startTime && values.startTime !== initial.startTime) {
    payload.startTime = values.startTime;
  }
  const endTime = values.isEndless ? null : values.endTime || null;
  if (endTime !== initial.endTime) payload.endTime = endTime;
  return payload;
};

// Only points quests can be created, and the schema requires their month, so it's always set here.
export const questDetailsFormValuesToCreatePayload = (
  values: QuestDetailsFormValues,
  timezone: string,
  venueUuid: string,
): ApiCreateQuestPayload => ({
  venue: venueUuid,
  type: "LEADERBOARD_POINTS",
  name: values.name,
  description: values.description,
  coverImage: values.coverImage?.uuid ?? null,
  climbTypes: values.climbTypes as ClimbType[],
  ...monthToPeriod(values.month!, timezone),
});
